import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {forkJoin} from 'rxjs';
import {SubmissionHttpService} from '../../forms/data-access/submission-http.service';
import {FormHttpService} from '../../forms/data-access/form-http.service';
import {FieldBlock, FormBlock, FormDefinition, GroupBlock} from '../../forms/shared/models/form-schema.model';
import {isFileRef, Submission, SubmissionFileRef} from '../../forms/shared/models/submission.model';
import {
  buildSubmitRequest, maskedFieldKeys, maskedFieldMap, requestedFileFieldKeys, storedFieldKeys,
  submissionToInitialValue
} from '../../forms/shared/services/submission-mapper';
import {readSubmissionFieldErrors} from '../../forms/shared/services/submission-errors';
import {attachmentTooLargeMessage, isAttachmentTooLarge} from '../../forms/shared/services/attachment-limits';
import {DynamicFormComponent} from '../../forms/runtime/components/dynamic-form/dynamic-form.component';
import {MaskedFieldsState} from '../../forms/runtime/services/masked-fields-state.service';
import {FormsGateHttpService} from '../../forms/data-access/forms-gate-http.service';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';

/**
 * Edit a **draft** submission and (re)submit it. Reached after revising a finalised submission (which creates the
 * draft) — see {@link EmployeeSubmissionDetailComponent}. Prefills the form with the draft's values; **masked**
 * sensitive fields that aren't touched are sent as `keep` so their stored value is preserved. On submit it
 * finalises (PUT, re-validated against the current form version); if valid, the original is superseded server-side.
 *
 * Attachments are handled out-of-band: file fields are stripped from the wizard and surfaced in a dedicated panel.
 * The carried file (re-encrypted into the revision server-side) is shown by name only — the submitting employee
 * can **replace** it but never view it (download is role-gated). A replacement uploads straight to the draft via
 * {@link SubmissionHttpService.uploadFile} (swap-in-place), independent of the finalise step.
 *
 * **The panel obeys `visibleWhen` (#503).** Splitting attachments out of the wizard also split them out of the
 * conditional logic: the panel listed every `file` block in the definition, once, at load. Everything else about
 * the rules worked — the same fields were correctly hidden in the wizard, the evaluator emitted a real verdict
 * for each of them, and #494 already used it to keep hidden attachments off the wire — so the request was the one
 * surface that never asked. See {@link applyAttachmentVisibility} for what it now asks and what happens to a file
 * whose question goes away.
 */
@Component({
  selector: 'app-employee-submission-edit',
  standalone: true,
  imports: [RouterLink, DynamicFormComponent],
  // Scopes a MaskedFieldsState to this screen so the field-renderer edit-gates the carried masked values.
  providers: [MaskedFieldsState],
  template: `
    <div class="absolute inset-0 overflow-y-auto p-3 sm:p-4 lg:p-6">
      <div class="mx-auto w-full max-w-4xl flex flex-col gap-4">
        <a [routerLink]="['..']"
           class="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          </svg>
          Meine Formulare
        </a>

        @if (loading) {
          <div class="flex items-center justify-center py-20">
            <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
            </svg>
          </div>
        } @else if (error || !definition) {
          <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm">
            <p class="text-sm font-semibold text-red-600">Das Formular konnte nicht geladen werden.</p>
            <button (click)="load()" class="mt-2 inline-flex items-center text-red-600 bg-red-100 hover:bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200">Erneut versuchen</button>
          </div>
        } @else {
          @if (errorMessage) {
            <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm">
              <p class="text-sm font-semibold text-red-600">{{ errorMessage }}</p>
            </div>
          }

          @if (allFileFields.length) {
            <section data-test="attachments" class="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-4 sm:p-5 shadow-sm">
              <div class="flex items-center gap-2">
                <svg class="h-4 w-4 text-theme-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                </svg>
                <h2 class="text-sm font-bold tracking-tight text-gray-800">Anhänge</h2>
              </div>
              @for (f of requestedFileFields; track f.key) {
                <div [attr.data-test-requested]="f.key"
                     class="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold text-gray-800">{{ f.label }}</p>
                    @if (f.current) {
                      <p class="truncate text-xs text-gray-500">
                        {{ f.replaced ? 'Neue Datei' : 'Übernommen' }}: {{ f.current.fileName }}
                        <span class="text-gray-500">· für Sie nicht einsehbar</span>
                      </p>
                    } @else {
                      <p class="text-xs text-gray-500">Keine Datei angehängt</p>
                    }
                    @if (f.error) {
                      <p data-test="attachment-error" class="mt-1 text-xs font-semibold text-red-500">{{ f.error }}</p>
                    }
                  </div>
                  <label class="inline-flex shrink-0 cursor-pointer items-center gap-2 self-start rounded-xl bg-theme-primary-50 px-3 py-2 text-sm font-bold text-theme-primary-700 transition-colors hover:bg-theme-primary-100"
                         [class.opacity-50]="f.uploading" [class.pointer-events-none]="f.uploading">
                    @if (f.uploading) {
                      <svg class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
                      </svg>
                      Wird hochgeladen…
                    } @else {
                      {{ f.current ? 'Datei ersetzen' : 'Datei hochladen' }}
                    }
                    <input type="file" class="hidden" [multiple]="false" [disabled]="f.uploading"
                           (change)="onFileSelected(f, $event)"/>
                  </label>
                </div>
              }

              @if (!requestedFileFields.length) {
                <p data-test="attachments-empty" class="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 p-3 text-xs text-gray-500">
                  Für Ihre Angaben werden derzeit keine Nachweise benötigt. Sollte sich das durch eine Änderung im
                  Formular ändern, erscheinen die passenden Uploads hier automatisch.
                </p>
              }

              @if (strandedFileFields.length) {
                <div data-test="attachments-stranded" class="flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50/70 p-3">
                  <p class="text-xs font-bold text-amber-700">Nicht mehr erforderlich</p>
                  @for (f of strandedFileFields; track f.key) {
                    <div [attr.data-test-stranded]="f.key" class="min-w-0">
                      <p class="truncate text-sm font-semibold text-gray-800">{{ f.label }}</p>
                      <p class="truncate text-xs text-gray-500">
                        {{ f.replaced ? 'Neue Datei' : 'Übernommen' }}: {{ f.current!.fileName }}
                        <span class="text-gray-500">· für Sie nicht einsehbar</span>
                      </p>
                    </div>
                  }
                  <p class="text-xs text-amber-700">
                    Diese Frage gilt für Ihre Angaben nicht mehr. Die bereits hochgeladene Datei wird nicht erneut
                    angefordert, bleibt aber an dieser Version gespeichert. Wenden Sie sich an Ihre Ansprechperson,
                    wenn sie gelöscht werden soll.
                  </p>
                </div>
              }
            </section>
          }

          <forms-dynamic-form class="block" [definition]="formDefinition!" [initialValue]="initialValue"
                              (valueChanged)="onFormValueChanged($event)"
                              (submitForm)="onSubmit($event)" (saveDraft)="onSaveDraft($event)"></forms-dynamic-form>
        }
      </div>
    </div>
  `
})
export class EmployeeSubmissionEditComponent implements OnInit {
  protected loading = true;
  protected error = false;
  protected errorMessage: string | null = null;
  protected definition: FormDefinition | null = null;
  /** The definition with file blocks removed — attachments are handled in the panel, not the wizard. */
  protected formDefinition: FormDefinition | null = null;
  /**
   * **Every** file field the definition declares, with its carried attachment and upload state — the durable
   * list, never the rendered one. The two rendered lists below are views onto it, so an entry's upload state
   * survives a question going away and coming back.
   */
  protected allFileFields: EditFileField[] = [];
  /** The attachments the current answers actually ask for — the only ones offered for upload (#503). */
  protected requestedFileFields: EditFileField[] = [];
  /** Attachments already on the draft whose question stopped applying (#503). Named, never silently dropped. */
  protected strandedFileFields: EditFileField[] = [];
  protected initialValue: any = {};

  /**
   * The wizard's live raw value, republished by {@link DynamicFormComponent.valueChanged}. Seeded from
   * {@link initialValue} so the very first paint is already filtered — an employee reopening a draft must not see
   * five obsolete document requests flash up before the form's first value event.
   */
  private rawValue: any = {};

  /** The live runtime form, so a server 422 can be pushed back onto the offending controls. */
  @ViewChild(DynamicFormComponent) private dynamicForm?: DynamicFormComponent;

  private id = '';
  private masked: Record<string, unknown> = {};
  /**
   * What the loaded draft already stores. Feeds {@link BuildSubmitOptions.stored} so a field that stopped
   * applying is CLEARED rather than omitted — on an update an omitted key means "leave it alone", so the
   * stale answer would otherwise survive the revision (#470).
   */
  private stored: ReadonlySet<string> = new Set<string>();
  private submitting = false;

  constructor(
    private route: ActivatedRoute,
    private http: SubmissionHttpService,
    private forms: FormHttpService,
    private alert: GlobalAlertService,
    private router: Router,
    private maskedState: MaskedFieldsState,
    private gate: FormsGateHttpService,
    private auth: AuthenticationService
  ) {
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.load();
  }

  load(): void {
    if (!this.id) {
      this.error = true;
      this.loading = false;
      return;
    }
    this.loading = true;
    this.error = false;
    this.errorMessage = null;

    this.http.get(this.id).subscribe({
      next: submission => {
        this.forms.getDefinition(submission.formId).subscribe({
          next: definition => {
            this.definition = definition;
            this.formDefinition = stripFileBlocks(definition);
            this.allFileFields = collectFileFields(definition).map(f => ({
              ...f,
              current: findAttachment(submission, f.key),
              replaced: false,
              uploading: false,
              error: null
            }));
            this.initialValue = submissionToInitialValue(definition, submission);
            // Filter before the first render, not on the wizard's first value event — see `rawValue`.
            this.rawValue = this.initialValue;
            this.applyAttachmentVisibility();
            this.masked = maskedFieldMap(submission);
            this.stored = storedFieldKeys(submission);
            // Bare field keys, not wire addresses: the renderer edit-gates by field, the mapper by row (#482).
            this.maskedState.set(maskedFieldKeys(submission));
            this.loading = false;
          },
          error: () => this.fail()
        });
      },
      error: () => this.fail()
    });
  }

  /**
   * The wizard's value changed — re-decide which attachments the form is still asking for (#503).
   *
   * Evaluated against {@link definition}, the **full** one. {@link formDefinition} has had the `file` blocks
   * stripped out of it, so the wizard's own {@link FormVisibilityState} verdict holds no entry for any of them —
   * this panel is the only place that can answer the question, and it has to ask it on every change rather than
   * once at load.
   */
  protected onFormValueChanged(rawValue: any): void {
    this.rawValue = rawValue;
    this.applyAttachmentVisibility();
  }

  /**
   * Splits {@link allFileFields} into what is offered and what is stranded, from the current answers.
   *
   * **An attachment is requested only while its field is visible.** The panel used to render every `file` block
   * in the definition unconditionally, so the Personalfragebogen asked an employee who had ticked "Keines davon"
   * for a Rentenbescheid, a Schulbescheinigung, an Immatrikulationsbescheinigung and a Pensionsbescheid — four
   * personal documents their own answers said they did not have. The rules were fine and the evaluator had
   * always emitted a verdict for `file` fields; the panel simply never read it.
   *
   * **A file already on the draft is never deleted here, and never quietly carried either.** A hidden field with
   * a stored attachment moves into {@link strandedFileFields}, which offers no upload affordance and says in so
   * many words that the file stays attached to this version. Deleting it would be destructive on a
   * retention-dated personnel record over an answer the employee may well flip back a second later, and there is
   * no attachment-delete endpoint to do it with; hiding it would be the silent carry #470 rules out for every
   * other kind of answer. Naming it is the only option that is neither.
   */
  private applyAttachmentVisibility(): void {
    if (!this.definition) {
      this.requestedFileFields = [];
      this.strandedFileFields = [];
      return;
    }

    const requested = requestedFileFieldKeys(this.definition, this.rawValue);
    this.requestedFileFields = this.allFileFields.filter(f => requested.has(f.key));
    // Only a *stored* attachment can strand. A hidden field with nothing attached is simply not asked about.
    this.strandedFileFields = this.allFileFields.filter(f => !requested.has(f.key) && !!f.current);
  }

  /**
   * Upload a replacement attachment for a file field — swaps it in place on the draft (server-side).
   *
   * **A file over {@link MAX_ATTACHMENT_BYTES} is refused before the request is made (#779)**, the same rule and
   * the same message the runtime form's own picker uses (`FieldRendererComponent.onFileChange`). This is the
   * *other* picker in the feature, and it is the one an employee reaches after being asked to correct something
   * — so it is the last place that can afford a blind failure. Left unchecked, an oversized pick dies at the
   * edge proxy, whose `413` carries no CORS header (CORS lives only at the gateway), so Angular sees a network
   * error with no status and the panel could only say "Die Datei konnte nicht hochgeladen werden." about a file
   * that will never upload however often it is retried. See `attachment-limits.ts` for why the cap is 10 MB and
   * not the server's 25.
   */
  onFileSelected(field: EditFileField, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (isAttachmentTooLarge(file)) {
      this.rejectOversizedPick(field, input, file);
      return;
    }

    field.uploading = true;
    field.error = null;
    this.http.uploadFile(this.id, field.key, file).subscribe({
      next: res => {
        field.uploading = false;
        field.replaced = true;
        field.current = {
          blobId: res.blobId, fileName: res.fileName, contentType: res.contentType, sizeBytes: res.sizeBytes
        };
        input.value = ''; // allow re-selecting the same file name again
        this.alert.createAndShow('Datei ersetzt', `„${res.fileName}“ wurde übernommen.`, AlertLevel.success, 2500);
      },
      error: () => {
        field.uploading = false;
        field.error = 'Die Datei konnte nicht hochgeladen werden. Bitte versuchen Sie es erneut.';
        input.value = '';
      }
    });
  }

  /**
   * Refuses an oversized pick: nothing is uploaded, no spinner is shown, and the field's own error row names the
   * file, its real size and the limit — the same channel a failed upload already uses, so the employee reads one
   * message in one place whatever the reason.
   *
   * **The carried attachment is kept.** Unlike the runtime form's picker, which drops a previously accepted file
   * because the native input has already replaced its own selection, what `current` holds here lives on the
   * server and was never touched: the replacement simply did not happen. Clearing it would claim a deletion that
   * did not occur, and there is no attachment-delete endpoint to make it true.
   *
   * **The native input is reset.** A file input fires no `change` event when the same file is chosen twice, so an
   * employee who shrinks the document and picks that very same path would otherwise be stuck looking at an error
   * nothing could clear — the reason the success and failure paths already reset it.
   */
  private rejectOversizedPick(field: EditFileField, input: HTMLInputElement, file: File): void {
    field.error = attachmentTooLargeMessage(file);
    input.value = '';
  }

  onSubmit(rawValue: any): void {
    if (!this.definition || this.submitting) return;
    this.submitting = true;
    this.errorMessage = null;
    const request = buildSubmitRequest(this.definition, rawValue, true, {masked: this.masked, stored: this.stored});
    this.http.update(this.id, request).subscribe({
      next: () => {
        this.submitting = false;
        this.alert.createAndShow('Eingereicht', 'Ihre neue Version wurde gespeichert.', AlertLevel.success, 2500);
        this.refreshGateThenLeave();
      },
      error: err => {
        this.submitting = false;
        this.errorMessage = this.describeFailure(err);
      }
    });
  }

  /**
   * Same per-field surfacing as the first-fill screen (#258) — this path had the identical gap: a `422`'s
   * `{fieldKey, code, message}` payload was dropped for one generic sentence, so nothing was marked. The shared
   * helper marks the offending controls; the banner keeps only what belonged to no field.
   */
  private describeFailure(err: unknown): string {
    const fieldErrors = readSubmissionFieldErrors(err);
    if (fieldErrors.length === 0) {
      return (err as {status?: number} | null)?.status === 422
        ? 'Bitte überprüfen Sie Ihre Angaben — einige Pflichtfelder fehlen oder sind ungültig.'
        : 'Das Formular konnte nicht gesendet werden. Bitte versuchen Sie es erneut.';
    }

    const unmatched = this.dynamicForm?.applyServerErrors(fieldErrors) ?? fieldErrors;
    return unmatched.length > 0
      ? unmatched.map(e => e.message).join(' ')
      : 'Bitte korrigieren Sie die rot markierten Felder.';
  }

  /**
   * Resubmitting clears a review flag in the DB, but the app-wide review-due banner is driven by a token claim
   * baked at login — a plain refresh re-reads the same stale token. So when a gate banner is active, re-evaluate
   * the gate live (reissue) and swap the token in before leaving, so a now-resolved flag stops showing. Best-
   * effort: if the reissue fails we still navigate (the per-submission flag is already correct from the API).
   */
  private refreshGateThenLeave(): void {
    if (!this.auth.isFormsReviewDue && !this.auth.isFormsRestricted) {
      this.leave();
      return;
    }
    this.gate.reissue().subscribe({
      next: token => {
        this.auth.applyReissuedSession(token);
        this.leave();
      },
      error: () => this.leave()
    });
  }

  private leave(): void {
    this.router.navigate(['/ep/submissions']).then();
  }

  onSaveDraft(rawValue: any): void {
    if (!this.definition || this.submitting) return;
    this.submitting = true;
    const request = buildSubmitRequest(this.definition, rawValue, false, {masked: this.masked, stored: this.stored});
    this.http.update(this.id, request).subscribe({
      next: () => {
        this.submitting = false;
        this.alert.createAndShow('Entwurf gespeichert', 'Sie können später hier weitermachen.', AlertLevel.success, 2500);
      },
      error: () => {
        this.submitting = false;
        this.alert.createAndShow('Fehler', 'Der Entwurf konnte nicht gespeichert werden.', AlertLevel.error, 3000);
      }
    });
  }

  private fail(): void {
    this.error = true;
    this.loading = false;
  }
}

/** A file field surfaced in the attachments panel — carried metadata + per-field upload state. */
interface EditFileField {
  key: string;
  label: string;
  /** The currently-attached file (carried from the prior version, or a just-uploaded replacement), or null. */
  current: SubmissionFileRef | null;
  /** True once the employee has uploaded a replacement in this session. */
  replaced: boolean;
  uploading: boolean;
  error: string | null;
}

/** Returns a copy of the definition with all file fields removed (recursing into groups/lists). */
function stripFileBlocks(definition: FormDefinition): FormDefinition {
  const filter = (blocks: FormBlock[]): FormBlock[] =>
    blocks
      .filter(b => b.type !== 'file')
      .map(b => (b.type === 'group' || b.type === 'list')
        ? {...b, blocks: filter((b as GroupBlock).blocks)}
        : b);
  return {...definition, pages: definition.pages.map(p => ({...p, blocks: filter(p.blocks)}))};
}

/** Flattens the file fields out of a definition (recursing into groups/lists) for the attachments panel. */
function collectFileFields(definition: FormDefinition): {key: string; label: string}[] {
  const out: {key: string; label: string}[] = [];
  const walk = (blocks: FormBlock[]) => {
    for (const block of blocks) {
      if (block.type === 'file') {
        const field = block as FieldBlock;
        out.push({key: field.key, label: block.label || field.key});
      } else if (block.type === 'group' || block.type === 'list') {
        walk((block as GroupBlock).blocks);
      }
    }
  };
  definition.pages.forEach(page => walk(page.blocks));
  return out;
}

/** Finds the attachment metadata stored for a field on a submission, or null. */
function findAttachment(submission: Submission, fieldKey: string): SubmissionFileRef | null {
  for (const group of submission.groups) {
    for (const value of group.values) {
      if (value.fieldKey === fieldKey && isFileRef(value.value)) return value.value;
    }
  }
  return null;
}
